<template>
  <div class="YoutubeIframePlayer">
    <div
      v-show="!state.error"
      class="YoutubeIframePlayer__iframeWrapper"
      :style="iframeWrapperStyles"
    >
      <div :id="youtubeIframeId" class="YoutubeIframePlayer__iframe"></div>
    </div>
    <div v-show="state.error" class="YoutubeIframePlayer__error">
      <p>動画を読み込みできませんでした。以下URLをご確認ください</p>
      <p>
        <a :href="`https://youtu.be/${videoId}`" target="_blank">
          https://youtu.be/{{ videoId }}
        </a>
      </p>
    </div>
  </div>
</template>

<script>
/**
 * YouTube IFrame Player API
 * @see https://developers.google.com/youtube/iframe_api_reference
 * @see https://developers.google.com/youtube/player_parameters.html#Parameters
 *
 * 【注意】
 * youtubeIframeIde DOMを維持し続ける必要があるため
 * v-showで表示操作を行う（v-ifを使用しない）
 */

const YOUTUBE_API_URL = 'https://www.youtube.com/iframe_api';
const YOUTUBE_DEFAULT_WIDTH = 640;
const YOUTUBE_DEFAULT_HEIGHT = 360;

export default {
  name: 'YoutubePlayer',

  props: {
    videoId: {
      type: String,
      default: '',
      required: true,
    },
    width: {
      type: [Number, String],
      default: YOUTUBE_DEFAULT_WIDTH,
    },
    height: {
      type: [Number, String],
      default: YOUTUBE_DEFAULT_HEIGHT,
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    mute: {
      type: Boolean,
      default: true,
    },
    controls: {
      type: Boolean,
      default: true,
    },
    disablekb: {
      type: Boolean,
      default: false,
    },
    // 秒数
    start: {
      type: Number,
      default: undefined,
    },
    // 秒数
    end: {
      type: Number,
      default: undefined,
    },
    // 全画面
    fs: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      player: null,
      script: null,
      state: {
        ready: false,
        loading: false,
        error: false,
      },
    };
  },

  computed: {
    youtubeIframeId() {
      return `youtube-iframe-${this.videoId}`;
    },
    iframeWrapperStyles() {
      return {
        width: !this.isNumberOfWidth() ? this.width : '',
        height: !this.isNumberOfHeight() ? this.height : '',
      };
    },
  },

  watch: {
    videoId: {
      handler(videoId) {
        if (videoId) {
          this.init();
        } else {
          this.pauseVideo();
        }
      },
      immediate: true,
    },
  },

  /**
   * 基本的にv-showでDOMキープ想定なので、呼び出されないはず。
   * だが、destroyしても動作する作りにしておく
   */
  beforeUnmount() {
    if (this.player) {
      this.player.stopVideo();
      this.player.destroy();
      this.player = null;
    }
    if (this.script && this.script.remove) {
      this.script.remove();
      window.YT = null;
    }
  },

  methods: {
    playVideo(player) {
      player = player || this.player;
      if (player) {
        player.playVideo();
      }
    },
    pauseVideo() {
      if (this.player) {
        this.player.pauseVideo();
      }
    },
    muteVideo() {
      if (this.player) {
        this.player.mute();
      }
    },
    unMuteVideo() {
      if (this.player) {
        this.player.unMute();
      }
    },
    /**
     * 再生中の動画の長さを秒数で返す
     * @returns {number}
     */
    getDuration() {
      return this.player ? this.player.getDuration() : 0;
    },

    /**
     * @typedef VideoOptions
     * @property {number} startSeconds
     * @property {number} endSeconds
     * @property {string} suggestedQuality - https://developers.google.com/youtube/iframe_api_reference?hl=ja#setPlaybackQuality
     */
    /**
     * @param {VideoOptions} options
     */
    loadVideo(options) {
      if (this.player && this.videoId) {
        const o = Object.assign(this.getDefaultVideoOptions(), options);
        this.player.loadVideoById(o);
      }
    },

    /**
     * @param {VideoOptions} options
     */
    cueVideo(options) {
      if (this.player && this.videoId) {
        const o = Object.assign(this.getDefaultVideoOptions(), options);
        this.player.cueVideoById(o);
      }
    },

    resetState() {
      this.state.loading = false;
      this.state.error = false;
    },

    init() {
      this.resetState();

      if (!this.state.ready) {
        this.setOnYouTubeIframeAPIReady();
        this.initApiScript();
      } else if (this.autoplay) {
        this.loadVideo();
      } else {
        this.cueVideo();
      }
    },

    initPlayer() {
      this.state.loading = true;
      this.state.error = false;

      const height = this.isNumberOfHeight()
        ? String(this.height)
        : YOUTUBE_DEFAULT_HEIGHT;
      const width = this.isNumberOfWidth()
        ? String(this.width)
        : YOUTUBE_DEFAULT_WIDTH;

      this.player = new window.YT.Player(this.youtubeIframeId, {
        height,
        width,
        playerVars: this.getPlayerVars(),
        events: {
          onReady: (e) => {
            this.state.loading = false;
            this.onReady(e);
          },
          onStateChange: this.onStateChange.bind(this),
          onError: (e) => {
            this.state.loading = false;
            this.state.error = true;
            this.onError(e);
          },
          // onPlaybackQualityChange: this.onPlaybackQualityChange.bind(this),
          // onPlaybackRateChange: this.onPlaybackRateChange.bind(this),
          // onApiChange: this.onApiChange.bind(this),
        },
      });
    },

    initApiScript() {
      // 2. This code loads the IFrame Player API code asynchronously.
      this.script = document.createElement('script');
      this.script.src = YOUTUBE_API_URL;

      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(this.script, firstScriptTag);
    },

    // 3. This function creates an <iframe> (and YouTube player)
    //    after the API code downloads.
    setOnYouTubeIframeAPIReady() {
      window.onYouTubeIframeAPIReady = this.initPlayer.bind(this);
    },

    // 4. The API will call this function when the video player is ready.
    onReady() {
      if (this.autoplay) {
        this.loadVideo();
      } else {
        this.cueVideo();
      }
      this.state.ready = true;
      this.$emit('ready', this.player);
    },

    // 5. The API calls this function when the player's state changes.
    //    The function indicates that when playing a video (state=1),
    //    the player should play for six seconds and then stop.
    onStateChange(event) {
      const ytState = window.YT.PlayerState;

      switch (event.data) {
        // 未開始
        case ytState.UNSTARTED:
          // console.log('change:state', 'unstarted');
          this.$emit('change:state', {
            state: 'unstarted',
            videoId: this.videoId,
          });
          break;
        // バッファリング中
        case ytState.BUFFERING:
          // console.log('change:state', 'buffering');
          this.$emit('change:state', {
            state: 'buffering',
            videoId: this.videoId,
          });
          break;
        // 再生中
        case ytState.PLAYING:
          // console.log('change:state', 'playing');
          this.$emit('change:state', {
            state: 'playing',
            videoId: this.videoId,
          });
          break;
        // 終了
        case ytState.ENDED:
          // console.log('change:state', 'ended');
          this.$emit('change:state', {
            state: 'ended',
            videoId: this.videoId,
          });
          break;
        // 停止
        case ytState.PAUSED:
          // console.log('change:state', 'paused');
          this.$emit('change:state', {
            state: 'paused',
            videoId: this.videoId,
          });
          break;
        // 頭出し済み
        case ytState.CUED:
          // console.log('change:state', 'cued');
          this.$emit('change:state', {
            state: 'cued',
            videoId: this.videoId,
          });
          break;
      }
    },

    onError(e) {
      console.error('YoutubePlayer: onError State ', e.data);
      this.$emit('error', this.player);
    },

    // /**
    //  * 動画の再生画質が変わると起動
    //  */
    // onPlaybackQualityChange() {},

    // /**
    //  * 動画の再生速度が変わると起動
    //  */
    // onPlaybackRateChange() {},

    // /**
    //  * プレーヤーが直接的な API メソッドでモジュールを読み込んだ（または読み込み解除した）ことを示すために発行される
    //  */
    // onApiChange() {},

    /**
     * @returns {boolean}
     */
    isNumberOfWidth() {
      return !Number.isNaN(this.width - 0);
    },
    /**
     * @returns {boolean}
     */
    isNumberOfHeight() {
      return !Number.isNaN(this.height - 0);
    },
    /**
     * @returns {object}
     */
    getPlayerVars() {
      const playerVars = {
        autoplay: this.castBoolToPlayerVarsValue(this.autoplay),
        // 自動再生はミュートが必須（ブラウザポリシーによる）
        mute: this.autoplay ? 1 : this.castBoolToPlayerVarsValue(this.mute),
        controls: this.castBoolToPlayerVarsValue(this.controls),
        disablekb: this.castBoolToPlayerVarsValue(this.disablekb),
        fs: this.castBoolToPlayerVarsValue(this.fs),
        loop: this.castBoolToPlayerVarsValue(this.loop),
        rel: 0,
      };
      if (typeof this.start !== 'undefined') {
        playerVars.start = this.start;
      }
      if (typeof this.end !== 'undefined') {
        playerVars.end = this.end;
      }

      return playerVars;
    },
    /**
     * @returns {object}
     */
    getDefaultVideoOptions() {
      const options = { videoId: this.videoId };

      if (typeof this.start !== 'undefined') {
        options.startSeconds = this.start;
      }
      if (typeof this.end !== 'undefined') {
        options.endSeconds = this.end;
      }

      return options;
    },
    /**
     * @param {boolean}
     * @returns {1|0}
     */
    castBoolToPlayerVarsValue(value) {
      return value ? 1 : 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.YoutubeIframePlayer {
  &__iframeWrapper {
    width: 100%;
    height: 100%;

    ::v-deep(iframe) {
      vertical-align: bottom;
      width: 100%;
      height: 100%;
    }
  }
  &__error {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 18px;
    p + p {
      margin-top: 0.5em;
    }
  }
}
</style>
